import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCapabilityScope } from "@/types/capabilities";
import type { CapabilityBulkDecision } from "@/types/capabilities";
import type { CapabilityOverride } from "@/services/capabilitiesApi";
import { Trash2 } from "lucide-react";

interface CapabilityOverridesTableProps {
  overrides: CapabilityOverride[];
  isLoading?: boolean;
  selectedIds: Set<number>;
  onSelectedIdsChange: (ids: Set<number>) => void;
  onDelete: (override: CapabilityOverride) => void;
  onBulkDecision: (decision: CapabilityBulkDecision) => void;
}

export function CapabilityOverridesTable({
  overrides,
  isLoading,
  selectedIds,
  onSelectedIdsChange,
  onDelete,
  onBulkDecision,
}: CapabilityOverridesTableProps) {
  const selectedVisible = overrides.filter((row) => selectedIds.has(row.id));
  const selectedCount = selectedVisible.length;
  const allSelected =
    overrides.length > 0 && selectedCount === overrides.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleRow = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedIdsChange(next);
  };

  const toggleAll = (checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) {
      overrides.forEach((row) => next.add(row.id));
    } else {
      overrides.forEach((row) => next.delete(row.id));
    }
    onSelectedIdsChange(next);
  };

  return (
    <div className="overflow-hidden rounded-md border">
      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/40 px-3 py-2">
          <p className="text-sm">
            <span className="font-medium tabular-nums">{selectedCount}</span>
            <span className="text-muted-foreground">
              {" "}
              selected — write one decision
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <div
              className="inline-flex rounded-md border border-input bg-background p-0.5"
              role="group"
              aria-label="Bulk decision"
            >
              <button
                type="button"
                className="rounded-sm px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onBulkDecision("inherit")}
              >
                Delete
              </button>
              <button
                type="button"
                className="rounded-sm px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onBulkDecision("allow")}
              >
                Allow
              </button>
              <button
                type="button"
                className="rounded-sm px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onBulkDecision("deny")}
              >
                Deny
              </button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSelectedIdsChange(new Set())}
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              {overrides.length > 0 && !isLoading && (
                <Checkbox
                  checked={
                    allSelected ? true : someSelected ? "indeterminate" : false
                  }
                  onCheckedChange={(checked) => toggleAll(checked === true)}
                  aria-label="Select all overrides"
                />
              )}
            </TableHead>
            <TableHead>Capability</TableHead>
            <TableHead>Scope</TableHead>
            <TableHead>State</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-16" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: 8 }).map((_, j) => (
                  <TableCell key={j}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : overrides.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={8}
                className="py-12 text-center text-muted-foreground"
              >
                No overrides — everything inherits its default.
              </TableCell>
            </TableRow>
          ) : (
            overrides.map((override) => {
              const isSelected = selectedIds.has(override.id);
              return (
                <TableRow
                  key={override.id}
                  data-state={isSelected ? "selected" : undefined}
                >
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleRow(override.id)}
                      aria-label={`Select override ${override.capability} ${formatCapabilityScope(override.scope_type, override.scope_id)}`}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {override.capability}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatCapabilityScope(
                      override.scope_type,
                      override.scope_id,
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        override.state === "allow" ? "default" : "destructive"
                      }
                    >
                      {override.state}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                    {override.reason}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {override.expires_at ?? (
                      <span className="italic">never</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(override.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      onClick={() => onDelete(override)}
                      aria-label="Delete override"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
